<template>
  <div
    class="emergency-container"
    dir="rtl"
  >
    <!-- Simple Header -->
    <div class="header">
      <div class="header-text">
        <h2>📞 شماره‌های اضطراری</h2>
        <div class="subtitle-section">
          <p>شماره‌هایی که در مواقع اضطراری با شما تماس گرفته می‌شود</p>
          <button
            class="help-toggle"
            @click="helpOpen = !helpOpen"
          >
            <span class="help-text">راهنما</span>
            <span class="help-arrow">{{ helpOpen ? '▼' : '◀' }}</span>
          </button>
        </div>

        <!-- Help Content - اینجا باز می‌شود -->
        <div
          v-if="helpOpen"
          class="help-content"
        >
          <div class="help-item">
            <p>📞 <strong>شماره اصلی:</strong> اولین شماره‌ای که در مواقع اضطراری تماس گرفته می‌شود</p>
          </div>
          <div class="help-item">
            <p>🔔 <strong>شماره‌های اضافی:</strong> در صورت عدم پاسخ شماره اول، با این شماره‌ها تماس گرفته می‌شود</p>
          </div>
          <div class="help-item">
            <p>⚡ <strong>هشدارها:</strong> شامل هشدار حرکت، سرعت، قطع برق و... می‌شود</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading indicator -->
    <div
      v-if="isLoadingData"
      class="loading-section"
    >
      <div class="loading-spinner">
        ⏳
      </div>
      <p>در حال بارگذاری اطلاعات...</p>
    </div>

    <!-- Phone Numbers Section -->
    <div
      v-else
      class="numbers-section"
    >
      <h3 class="section-title">
        📱 شماره‌های تماس
      </h3>

      <div class="phone-list">
        <!-- Phone 1 -->
        <div class="phone-item">
          <div class="phone-label">
            <span>شماره اصلی *</span>
            <span class="number-badge primary">1</span>
          </div>
          <input
            v-model="phone1"
            type="tel"
            class="phone-input"
            placeholder="مثال: 09123456789"
            maxlength="11"
            @input="formatPhoneInput"
          >
          <div
            v-if="errors.phone1"
            class="error-text"
          >
            {{ errors.phone1 }}
          </div>
        </div>

        <!-- Phone 2 -->
        <div class="phone-item">
          <div class="phone-label">
            <span>شماره دوم (اختیاری)</span>
            <span class="number-badge">2</span>
          </div>
          <input
            v-model="phone2"
            type="tel"
            class="phone-input"
            placeholder="مثال: 09123456789"
            maxlength="11"
            @input="formatPhoneInput"
          >
          <div
            v-if="errors.phone2"
            class="error-text"
          >
            {{ errors.phone2 }}
          </div>
        </div>

        <!-- Phone 3 -->
        <div class="phone-item">
          <div class="phone-label">
            <span>شماره سوم (اختیاری)</span>
            <span class="number-badge">3</span>
          </div>
          <input
            v-model="phone3"
            type="tel"
            class="phone-input"
            placeholder="مثال: 09123456789"
            maxlength="11"
            @input="formatPhoneInput"
          >
          <div
            v-if="errors.phone3"
            class="error-text"
          >
            {{ errors.phone3 }}
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div
      v-if="!isLoadingData"
      class="actions"
    >
      <button
        class="btn btn-save"
        :disabled="!canSave || isLoading"
        @click="saveNumbers"
      >
        <span v-if="!isLoading">💾 ذخیره شماره‌ها</span>
        <span v-else>⏳ در حال ذخیره...</span>
      </button>

      <button
        v-if="hasEmergencyNumbers"
        class="btn btn-delete"
        :disabled="isLoading"
        @click="showDeleteConfirm = true"
      >
        🗑️ حذف همه
      </button>
    </div>

    <!-- Delete Confirmation -->
    <div
      v-if="showDeleteConfirm"
      class="overlay"
      @click="showDeleteConfirm = false"
    >
      <div
        class="confirm-box"
        @click.stop
      >
        <div class="confirm-content">
          <div class="confirm-icon">
            ⚠️
          </div>
          <h3>حذف شماره‌ها</h3>
          <p>آیا مطمئن هستید که می‌خواهید همه شماره‌های اضطراری را حذف کنید؟</p>
          <p class="warning-note">
            ⚠️ پس از حذف، هیچ هشداری دریافت نخواهید کرد
          </p>
          <div class="confirm-actions">
            <button
              class="btn btn-cancel"
              @click="showDeleteConfirm = false"
            >
              انصراف
            </button>
            <button
              class="btn btn-confirm-delete"
              @click="deleteNumbers"
            >
              حذف کن
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import axios from 'axios'
import authHeader from '@/services/auth-header'

export default {
  name: 'EmergencyNumberSettings',
  props: {
    selectedImei: {
      type: String,
      required: true,
    },
    deviceInfo: {
      type: Object,
      required: false, // تغییر از true به false
      default: () => null, // اضافه کردن default
    },
  },
  data() {
    return {
      phone1: '',
      phone2: '',
      phone3: '',
      isLoading: false,
      isLoadingData: false,
      showDeleteConfirm: false,
      helpOpen: false,
      errors: {
        phone1: '',
        phone2: '',
        phone3: '',
      },
    }
  },
  computed: {
    hasEmergencyNumbers() {
      return !!(this.phone1 || this.phone2 || this.phone3)
    },
    canSave() {
      return this.phone1 && this.isValidPhone(this.phone1) && !this.hasErrors
    },
    hasErrors() {
      return this.errors.phone1 || this.errors.phone2 || this.errors.phone3
    },
  },
  watch: {
    selectedImei: {
      handler(newVal, oldVal) {
        // console.log('🔄 selectedImei تغییر کرد:', { oldVal, newVal })
        if (newVal) { // حذف && this.deviceInfo
          this.loadDetail()
        }
      },
    },

    deviceInfo: {
      handler(newVal, oldVal) {
        // console.log('🔄 deviceInfo تغییر کرد:', {
        //   oldVal: oldVal?.imei,
        //   newVal: newVal?.imei,
        // })
        // این watch را غیرفعال می‌کنیم چون selectedImei کافی است
        // if (newVal && this.selectedImei) {
        //   this.loadDetail()
        // }
      },
      deep: true,
    },
  },
  mounted() {
    // حتی اگر deviceInfo نیامده باشه، اگر selectedImei داریم تلاش کن
    if (this.selectedImei) {
      this.loadDetail()
    }
  },
  methods: {
    async loadDetail() {
      // فقط selectedImei کافیه برای لود کردن شماره‌ها
      if (!this.selectedImei) {
        return
      }

      this.isLoadingData = true

      try {
        await this.loadSavedEmergencyNumbers()
      } catch (error) {
        this.showToast('خطا در بارگذاری شماره‌های اضطراری', 'error')
      } finally {
        this.isLoadingData = false
      }
    },

    async loadSavedEmergencyNumbers() {
      try {
        const response = await this.apiGetEmergencyNumbers(this.selectedImei)

        // بررسی ساختار صحیح response
        if (response.status === 'success' && response.result?.emergencyNumbers) {
          const emergency = response.result.emergencyNumbers

          // شماره‌های دریافتی از سرور را در فیلدها قرار بده
          this.phone1 = emergency.phone1 || ''
          this.phone2 = emergency.phone2 || ''
          this.phone3 = emergency.phone3 || ''
        } else {
          // اگر هیچ شماره‌ای در سرور نیست، فیلدها خالی بمانند
          this.phone1 = ''
          this.phone2 = ''
          this.phone3 = ''
        }
      } catch (error) {
        // در صورت خطا، فیلدها خالی بمانند
        this.phone1 = ''
        this.phone2 = ''
        this.phone3 = ''
      }
    },

    // API Methods با Authentication
    async apiGetEmergencyNumbers(imei) {
      try {
        const response = await axios.post(
          'https://s1.4-rah.com/device/emergency-numbers',
          { imei },
          { headers: authHeader() },
        )

        return response.data
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    async apiSendCommand(imei, commandName) {
      try {
        const response = await axios.post(
          'https://s1.4-rah.com/device/command',
          {
            imei,
            commandName,
          },
          { headers: authHeader() },
        )
        return response.data
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    // مدیریت خطاهای API
    handleApiError(error) {
      let message = 'خطای غیرمنتظره رخ داد'

      if (error.response) {
        // سرور پاسخ داده اما با کد خطا
        switch (error.response.status) {
          case 401:
            message = 'عدم احراز هویت. لطفا دوباره وارد شوید'
            break
          case 403:
            message = 'شما مجوز دسترسی به این بخش را ندارید'
            break
          case 404:
            message = 'دستگاه یافت نشد'
            break
          case 500:
            message = 'خطای سرور. لطفا بعداً تلاش کنید'
            break
          default:
            message = error.response.data?.message || 'خطا در ارتباط با سرور'
        }
      } else if (error.request) {
        // درخواست ارسال شده اما پاسخی دریافت نشده
        message = 'عدم ارتباط با سرور. اتصال اینترنت را بررسی کنید'
      }

      this.showToast(message, 'error')
    },

    formatPhoneInput(event) {
      const input = event.target
      const field = input.dataset.field || this.getFieldFromValue(input.value)

      // Clear previous error
      if (field) {
        this.errors[field] = ''
      }

      // Validate if has value
      if (input.value) {
        this.validatePhone(input.value, field)
      }
    },

    getFieldFromValue(value) {
      if (this.phone1 === value) return 'phone1'
      if (this.phone2 === value) return 'phone2'
      if (this.phone3 === value) return 'phone3'
      return null
    },

    validatePhone(phone, field) {
      if (!this.isValidPhone(phone)) {
        this.errors[field] = 'شماره باید با 09 شروع شده و 11 رقم باشد'
        return false
      }
      return true
    },

    isValidPhone(phone) {
      return /^09\d{9}$/.test(phone)
    },

    async saveNumbers() {
      // Validate main phone
      if (!this.phone1) {
        this.errors.phone1 = 'شماره اصلی الزامی است'
        return
      }

      if (!this.validatePhone(this.phone1, 'phone1')) return
      if (this.phone2 && !this.validatePhone(this.phone2, 'phone2')) return
      if (this.phone3 && !this.validatePhone(this.phone3, 'phone3')) return

      this.isLoading = true

      try {
        const command = `SOS,A,${this.phone1},${this.phone2 || ''},${this.phone3 || ''}#`

        const response = await this.apiSendCommand(this.selectedImei, command)

        if (response.status === 'success') {
          this.showToast('شماره‌های اضطراری با موفقیت ذخیره شد! 🎉', 'success')

          this.$emit('command-sent', {
            command,
            success: true,
          })
        } else {
          this.showToast('خطا در ذخیره شماره‌ها', 'error')
        }
      } catch (error) {
        // خطا در handleApiError نمایش داده می‌شود
        this.$emit('command-sent', { command: '', success: false, error })
      } finally {
        this.isLoading = false
      }
    },

    async deleteNumbers() {
      this.showDeleteConfirm = false
      this.isLoading = true

      try {
        const command = 'SOS,D,,,#'

        const response = await this.apiSendCommand(this.selectedImei, command)

        if (response.status === 'success') {
          // Clear all phones
          this.phone1 = ''
          this.phone2 = ''
          this.phone3 = ''

          this.showToast('تمام شماره‌ها با موفقیت حذف شد', 'success')

          this.$emit('command-sent', { command, success: true })
        } else {
          this.showToast('خطا در حذف شماره‌ها', 'error')
        }
      } catch (error) {
        // خطا در handleApiError نمایش داده می‌شود
        this.$emit('command-sent', { command: '', success: false, error })
      } finally {
        this.isLoading = false
      }
    },

    // Toast notification method
    showToast(message, type = 'info') {
      const variants = {
        success: 'success',
        error: 'danger',
        warning: 'warning',
        info: 'info',
      }

      const icons = {
        success: 'CheckIcon',
        error: 'XIcon',
        warning: 'AlertTriangleIcon',
        info: 'InfoIcon',
      }

      const titles = {
        success: 'موفقیت',
        error: 'خطا',
        warning: 'هشدار',
        info: 'اطلاع',
      }

      try {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: titles[type] || 'اطلاع',
            text: message,
            icon: icons[type] || 'InfoIcon',
            variant: variants[type] || 'info',
          },
        })
      } catch (error) {
        // Fallback: نمایش alert معمولی
        alert(`${titles[type]}: ${message}`)
      }
    },
  },
}
</script>

<style src="@/assets/styles/emergency-numbers.css" scoped></style>
